import React, { useState } from "react";
import moment from "moment-timezone";
import { useDocument } from "../loan_tool";
import { useUser } from "../../../hooks/user";
import { add_days } from "../../../constants/utils";
import {
  REALTOR_FIELDS,
  isBlankRealtor,
  mergeRealtor,
  searchRealtors
} from "../../../constants/realtor_directory";
import SaveLoan from "../save_loan";
import Image from "../../ui/image";
import "./css/ba_forms.scss";

// (XXX) XXX-XXXX progressive formatting — same behavior as the letters.
function formatPhone(value) {
  const d = (value || "").replace(/\D/g, "").slice(0, 10);
  if (d.length >= 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}

// Format a stored date to M/D/YY; "TBD" when empty; raw text if unparseable.
function fmtDate(value) {
  if (!value) return "TBD";
  let m = moment(
    value,
    ["YYYY-MM-DD", "MM/DD/YYYY", "M/D/YYYY", "YYYY-MM-DDTHH:mm:ss"],
    true
  );
  if (!m.isValid()) m = moment(value); // lenient fallback for ISO / other forms
  return m.isValid() ? m.format("M/D/YY") : String(value);
}

export default function RealtorAgentInformation() {
  const [document, setDocument] = useDocument();
  const [
    { companyProfile, companyProfileError },
    { saveCompanyProfile }
  ] = useUser();
  // Which agent block's name type-ahead is open (role key), if any.
  const [openRole, setOpenRole] = useState(null);
  const applicant = document.applicant || {};
  const rc = document.realtor_contacts || {};
  const directory = (companyProfile && companyProfile.realtor_directory) || [];

  // Header dates (auto-pulled; TBD when missing).
  const closeOfEscrow = fmtDate(applicant.estimated_close_of_escrow);
  const fundDate = fmtDate(applicant.fund_date);
  const lockExpires = fmtDate(
    add_days(applicant.loan_lock_date, applicant.pricing_lock_period)
  );

  const today = new Date();
  const dateStr = `${today.toLocaleString("en-US", { month: "long" })} ${today.getDate()}, ${today.getFullYear()}`;

  function updateRealtor(role, field, value) {
    const realtor_contacts = {
      ...rc,
      [role]: { ...(rc[role] || {}), [field]: value }
    };
    setDocument({ ...document, realtor_contacts });
  }

  // Fill an agent block from a directory pick (all six fields at once).
  function fillFromDirectory(role, r) {
    const next = { ...(rc[role] || {}) };
    REALTOR_FIELDS.forEach(f => (next[f] = r[f] || ""));
    setDocument({ ...document, realtor_contacts: { ...rc, [role]: next } });
    setOpenRole(null);
  }

  // Auto-add / update the company Realtor Directory on Save. A realtor is a person,
  // not a role, so BOTH blocks feed the same list (deduped on License #).
  function autoAddRealtors() {
    if (!companyProfile) return;
    let dir = directory;
    ["buyer_agent", "seller_agent"].forEach(role => {
      const agent = rc[role];
      if (agent && !isBlankRealtor(agent)) dir = mergeRealtor(dir, agent);
    });
    if (dir !== directory) {
      saveCompanyProfile({ ...companyProfile, realtor_directory: dir });
    }
  }

  function field(role, key, label, placeholder, isPhone, required) {
    const value = (rc[role] || {})[key] || "";
    return (
      <div className="ra-field">
        <div className="ra-field-label">
          {label}
          {required && <span className="ra-req">*</span>}
        </div>
        <input
          className="ra-field-input"
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={e =>
            updateRealtor(role, key, isPhone ? formatPhone(e.target.value) : e.target.value)
          }
        />
      </div>
    );
  }

  // Name field with type-ahead against the company Realtor Directory. Matches on
  // first name, last name or company; picking one fills all six fields.
  function nameField(role) {
    const value = (rc[role] || {}).name || "";
    const matches =
      openRole === role && value.trim()
        ? searchRealtors(directory, value).slice(0, 6)
        : [];
    return (
      <div className="ra-field ra-typeahead">
        <div className="ra-field-label">
          Name<span className="ra-req">*</span>
        </div>
        <input
          className="ra-field-input"
          type="text"
          value={value}
          placeholder="(Name)"
          autoComplete="off"
          onChange={e => {
            updateRealtor(role, "name", e.target.value);
            setOpenRole(role);
          }}
          onFocus={() => setOpenRole(role)}
          onBlur={() => window.setTimeout(() => setOpenRole(null), 150)}
        />
        {matches.length > 0 && (
          <div className="ra-dd no-print">
            {matches.map((r, i) => (
              <div
                key={i}
                className="ra-dd-item"
                onMouseDown={() => fillFromDirectory(role, r)}
              >
                <span className="ra-dd-name">{r.name}</span>
                {r.firm ? ` — ${r.firm}` : ""}
                {r.license ? <span className="ra-dd-sub"> · {r.license}</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function agentBlock(role, title, note) {
    return (
      <div className="ra-block">
        <div className="ra-block-title">
          {title}
          {note && <span className="ra-block-note">{note}</span>}
        </div>
        {nameField(role)}
        {field(role, "firm", "Realty Firm", "(Real Estate Company Name)", false, true)}
        {field(role, "office_phone", "Office Ph", "(XXX) XXX-XXXX", true, true)}
        {field(role, "cell_phone", "Cell Ph", "(XXX) XXX-XXXX", true, true)}
        {field(role, "email", "Email", "(Email Address)", false, true)}
        {field(role, "license", "License #", "(License #)", false, true)}
      </div>
    );
  }

  function coordinatorBlock(role) {
    return (
      <>
        <div className="ra-sub-title">
          Transaction Coordinator <span className="ra-sub-note">(info only)</span>
        </div>
        {field(role, "name", "Name", "(Name)")}
        {field(role, "cell_phone", "Cell Ph", "(XXX) XXX-XXXX", true)}
        {field(role, "email", "Email", "(Email Address)")}
      </>
    );
  }

  return (
    <div className="ra-doc">
      {/* Header — logo upper-left, Loan Set Up style, dates on the right */}
      <div className="ra-header">
        <div className="ra-header-left">
          <div className="ra-logo">
            <Image url="company/company-logo" className="ra-logo-img" />
          </div>
          <div className="ra-header-title">Loan Set Up</div>
          <div className="ra-header-sub">Purchase</div>
        </div>
        <div className="ra-header-right">
          <div className="ra-date-row">
            <span className="ra-date-label">Close of Escrow:</span>
            <span className="ra-date-value">{closeOfEscrow}</span>
          </div>
          <div className="ra-date-row">
            <span className="ra-date-label">Fund Date:</span>
            <span className="ra-date-value">{fundDate}</span>
          </div>
          <div className="ra-date-row">
            <span className="ra-date-label">Lock Expires:</span>
            <span className="ra-date-value">{lockExpires}</span>
          </div>
        </div>
      </div>
      <div className="ra-date-line">{dateStr}</div>

      <div className="ra-form-title">Realtor Agent Information</div>

      <div className="ra-columns">
        <div className="ra-column">
          {agentBlock(
            "buyer_agent",
            "Listing Agent",
            " → fills the Open House Flyer"
          )}
          {coordinatorBlock("buyer_coordinator")}
        </div>
        <div className="ra-column">
          {agentBlock(
            "seller_agent",
            "Buyer's Agent (Selling Agent)",
            " → fills the letters"
          )}
          {coordinatorBlock("seller_coordinator")}
        </div>
      </div>

      <div className="ra-required-note">
        <span className="ra-req">*</span> is a Required Field. You can still Save and
        finish later.
      </div>

      {companyProfileError && (
        <div className="ra-dir-error no-print">
          Realtor Directory not saved: {companyProfileError}
        </div>
      )}

      <div className="ra-actions no-print">
        <SaveLoan className="button" name="Save" onSave={autoAddRealtors} />
        <button className="button" onClick={() => window.print()}>
          Print
        </button>
      </div>
    </div>
  );
}
